import { useCallback, useState } from 'react';
import log from 'loglevel';
import jwtDecode from 'jwt-decode';
import { PaymentFormData } from '../types/PaymentFormData';
import { INewCardFormData } from '../types/INewCardFormData';
import { ISelectCardFormData } from '../types/ISelectCardFormData';
import { useStores } from '../../../../../app/root/domain/stores/useStores';
import { trackPaymentSubmitNewCard } from '../../shared/analytics/custom/trackPaymentSubmit';
import { trackPaymentMethod } from '../../shared/analytics/e-commerce/trackPaymentMethod';
import { useNavigate } from '../../../../../shared/router/useNavigate';
import { IDecodedJWTCardInfo } from '../types/IDecodedJWTCardInfo';
import { usePaymentContext } from '../PaymentContext';
import { logError } from '../../../../../shared/error/logError';
import { useRemoveOfferPromoCode } from '../../../../../shared/account/domain/hooks/useRemoveOfferPromoCode';

export function usePaymentSubmit(nextUrl: string) {
	const [paymentError, setPaymentError] = useState<undefined | Error>(
		undefined,
	);
	const { newBookingStore } = useStores();
	const submitNewCard = useSubmitNewCard();
	const submitSelectedCard = useSubmitSelectedCard();
	const { clearPromocode } = useRemoveOfferPromoCode();

	const navigate = useNavigate();

	const submit = useCallback(
		async (data: PaymentFormData) => {
			try {
				if ('tokenizedCardId' in data) {
					await submitSelectedCard(data);
				} else {
					await submitNewCard(data);
					trackPaymentSubmitNewCard(data);
				}

				clearPromocode();

				navigate(nextUrl);
			} catch (error) {
				setPaymentError(error as Error);
			}
		},
		[clearPromocode, navigate, nextUrl, submitNewCard, submitSelectedCard],
	);

	const proceedWithoutPay = useCallback(async () => {
		try {
			await newBookingStore.submit();

			clearPromocode();
			navigate(nextUrl);
		} catch (error) {
			setPaymentError(error as Error);
		}
	}, [clearPromocode, navigate, newBookingStore, nextUrl]);

	return { paymentError, submit, proceedWithoutPay };
}

export const usePaymentSubmitApp = (nextUrl?: string | string[]) => {
	const [paymentError, setPaymentError] = useState<undefined | Error>(
		undefined,
	);
	const submitNewCard = useSubmitNewCard();

	const getToken = useGetMicroformToken();

	const { storeCard } = usePaymentContext();

	const sendErrorToApp = () => {
		if (nextUrl && typeof nextUrl === 'string') {
			const url = new URL(nextUrl);
			url.searchParams.set('error', 'true');
			window.location.replace(url.toString());
		}
	};

	const submit = useCallback(
		async (data: INewCardFormData) => {
			try {
				setPaymentError(undefined);

				if (!nextUrl || typeof nextUrl !== 'string') {
					throw new Error('Callback URL is not defined');
				}
				const url = new URL(nextUrl);
				const token = await getToken(data);
				url.searchParams.set('token', token);
				url.searchParams.set('storeCard', String(storeCard));
				window.location.replace(url.toString());
			} catch (error) {
				sendErrorToApp();
				setPaymentError(error as Error);
			}
		},
		[submitNewCard, nextUrl, storeCard],
	);

	return { paymentError, submit, sendErrorToApp };
};

function useGetMicroformToken(): (data: INewCardFormData) => Promise<string> {
	const { microformInstance } = usePaymentContext();

	return useCallback(
		(data: INewCardFormData) =>
			new Promise((resolve, reject) => {
				if (!microformInstance || !data.expiryDate) {
					throw new Error('Microform instance not found');
				}

				microformInstance.createToken(
					{
						expirationMonth: `${data.expiryDate.month}`,
						expirationYear: `20${data.expiryDate.year}`,
					},
					(MicroformError, token) => {
						if (MicroformError) {
							const errorMessage =
								MicroformError.message || 'Failed to get microform token';
							const error = new Error(errorMessage);
							reject(error);
							logError(error);
						} else {
							log.info('Tokenized new card', token);
							resolve(token);
						}
					},
				);
			}),
		[microformInstance],
	);
}

function useSubmitNewCard() {
	const { tokenizedCardsStore, newBookingStore } = useStores();
	const { microformInstance } = usePaymentContext();
	const getToken = useGetMicroformToken();

	return useCallback(
		async (formData: INewCardFormData) => {
			const token = await getToken(formData);
			const { saveCreditCard, ...cardData } = formData;

			const { content } = jwtDecode<{
				content: { paymentInformation: { card: IDecodedJWTCardInfo } };
			}>(token);
			const jwtData = content.paymentInformation.card;
			await newBookingStore.submit({
				token,
				tokenType: 'transient',
				maskedCreditCardNumber: jwtData.number.maskedValue,
				creditCardType: cardData.creditCardType,
			});

			if (saveCreditCard && newBookingStore.booking.token) {
				await tokenizedCardsStore.save({
					creditCardType: cardData.creditCardType,
					maskedCreditCardNumber: jwtData.number.maskedValue,
					token: newBookingStore.booking.token,
					expiryDate: {
						month: jwtData.expirationMonth.value,
						year: jwtData.expirationYear.value,
					},
				});
			}

			trackPaymentMethod({
				isNewCard: true,
				cardType: cardData.creditCardType,
				booking: newBookingStore.booking,
			});
		},
		[tokenizedCardsStore, newBookingStore, microformInstance],
	);
}

function useSubmitSelectedCard() {
	const { newBookingStore, tokenizedCardsStore } = useStores();

	return useCallback(
		async (formData: ISelectCardFormData) => {
			log.info('Selected tokenized card', formData);

			const card = tokenizedCardsStore.findById(formData.tokenizedCardId);

			if (!card) {
				throw new Error('Invalid tokenized card ID');
			}

			await newBookingStore.submit({
				token: card.token,
				tokenType: 'reusable',
				maskedCreditCardNumber: card.maskedCreditCardNumber,
				creditCardType: card.creditCardType,
			});

			trackPaymentMethod({
				isNewCard: false,
				cardType: card.creditCardType,
				booking: newBookingStore.booking,
			});
		},
		[newBookingStore],
	);
}
